import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

const TIERS = ['ELITE FIT', 'STRONG FIT', 'GOOD FIT', 'MARGINAL FIT', 'POOR FIT'];
const tierColor = (t) => ({
  'ELITE FIT': '#00E676',
  'STRONG FIT': '#00D4FF',
  'GOOD FIT': '#FACC15',
  'MARGINAL FIT': '#FB923C',
  'POOR FIT': '#F87171',
}[t] || '#94A3B8');

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString()}`);
const canView = (role) => ['super_admin', 'broker', 'agent'].includes(role);

/* ------------------------------------------------------------------ */
/* Drafts list (/listing-lab)                                          */
/* ------------------------------------------------------------------ */
function DraftsList() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/api/listing-lab/drafts`);
        if (res.ok) setDrafts(await res.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
            🧪 Listing Lab
          </h1>
          <p className="text-[#B0BEC5] text-sm mt-1">
            Pre-listing analysis. Score a draft home against your active buyers, tune the
            price, and see demand — all privately. <span className="text-[#00D4FF]">Buyers never see drafts.</span>
          </p>
        </div>

        {loading ? (
          <div className="text-[#00D4FF] py-20 text-center">Loading…</div>
        ) : drafts.length === 0 ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16 px-6">
            <p className="text-[#B0BEC5] text-lg mb-2">No drafts yet</p>
            <p className="text-gray-500 mb-4">
              Add a home from the Properties page and choose <b>“Save as Listing Lab draft”</b> to analyze it here.
            </p>
            <Link to="/properties" className="inline-block px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] text-sm">
              Go to Properties
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {drafts.map((d) => (
              <Link key={d.id} to={`/listing-lab/${d.id}`}
                className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 border-l-4 border-l-[#00D4FF] p-4 hover:border-[#00D4FF]/60 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{d.address || 'Untitled draft'}</div>
                    <div className="text-gray-400 text-xs truncate">
                      {[d.city, d.state].filter(Boolean).join(', ')}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30 whitespace-nowrap">DRAFT</span>
                </div>
                <div className="text-[#00E676] font-bold mt-2">{money(d.price)}</div>
                <div className="text-gray-400 text-xs mt-1">
                  {[d.beds != null ? `${d.beds} bd` : null, d.baths != null ? `${d.baths} ba` : null,
                    d.sqft != null ? `${Number(d.sqft).toLocaleString()} sqft` : null].filter(Boolean).join(' · ')}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Demand summary + named matches                                      */
/* ------------------------------------------------------------------ */
function DemandPanel({ demand, evaluated }) {
  if (!demand) return null;
  const bd = demand.tier_breakdown || {};
  const total = demand.total_matches || 0;
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-white font-semibold text-lg">Buyer Demand</h2>
        <span className="text-gray-500 text-xs">{evaluated} active buyers evaluated</span>
      </div>
      <div className="flex items-end gap-3 mt-2">
        <div className="text-[#00E676] text-4xl font-bold leading-none">{total}</div>
        <div className="text-gray-400 text-sm mb-0.5">matching buyer{total === 1 ? '' : 's'}</div>
        <div className="ml-auto text-right">
          <div className="text-[#00D4FF] text-2xl font-bold leading-none">{demand.strong_or_better || 0}</div>
          <div className="text-gray-500 text-[11px]">strong or better</div>
        </div>
      </div>

      {/* tier breakdown */}
      <div className="flex flex-wrap gap-2 mt-4">
        {TIERS.map((t) => (
          <div key={t} className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#0A0E27] border border-[#546E7A]/30">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: tierColor(t) }} />
            <span className="text-gray-300 text-xs">{t.replace(' FIT', '')}</span>
            <span className="text-white font-semibold text-xs">{bd[t] || 0}</span>
          </div>
        ))}
      </div>

      {/* named matches — tiers only, never buyer criteria */}
      <div className="mt-4">
        <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">Matched Buyers</div>
        {(!demand.matches || demand.matches.length === 0) ? (
          <div className="text-gray-500 text-sm">No buyers match yet — try adjusting the price.</div>
        ) : (
          <div className="divide-y divide-[#546E7A]/20 max-h-72 overflow-y-auto">
            {demand.matches.map((m) => (
              <div key={m.buyer_id} className="flex items-center justify-between py-2 gap-2">
                <Link to={`/match-board?buyer=${m.buyer_id}`} className="text-white text-sm hover:text-[#00E676] truncate">
                  {m.buyer_name}
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-semibold" style={{ color: tierColor(m.tier) }}>
                    {Math.round((Number(m.fit_score) / 9) * 100)}%
                  </span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium border"
                    style={{ color: tierColor(m.tier), borderColor: tierColor(m.tier) + '55' }}>
                    {m.tier}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Demand-at-price readout                                             */
/* ------------------------------------------------------------------ */
function DemandAtPrice({ points, currentPrice }) {
  if (!points || points.length === 0) return null;
  const maxTotal = Math.max(1, ...points.map((p) => p.total_matches || 0));
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
      <h2 className="text-white font-semibold text-lg mb-1">Demand at Price</h2>
      <p className="text-gray-500 text-xs mb-3">How many buyers match as the list price moves.</p>
      <div className="space-y-2">
        {points.map((p) => {
          const isCur = currentPrice != null && Math.abs(p.price - currentPrice) < 1;
          const pct = Math.round(((p.total_matches || 0) / maxTotal) * 100);
          return (
            <div key={p.price} className="flex items-center gap-3">
              <div className={`w-24 text-right text-sm shrink-0 ${isCur ? 'text-[#00E676] font-bold' : 'text-gray-300'}`}>
                {money(p.price)}
              </div>
              <div className="flex-1 bg-[#0A0E27] rounded h-5 overflow-hidden border border-[#546E7A]/20">
                <div className="h-full rounded flex items-center"
                  style={{ width: `${Math.max(pct, 6)}%`, background: isCur ? '#00E676' : '#00D4FF' }}>
                  <span className="text-[#0A0E27] text-[11px] font-bold px-2">{p.total_matches}</span>
                </div>
              </div>
              <div className="w-24 text-xs text-gray-400 shrink-0 text-right">
                {p.strong_or_better} strong+
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Comparables panel                                                   */
/* ------------------------------------------------------------------ */
function Comparables({ comps }) {
  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
      <h2 className="text-white font-semibold text-lg mb-1">Comparable Active Listings</h2>
      <p className="text-gray-500 text-xs mb-3">
        Similar active homes for pricing context. (Full MLS comparables arrive with the MLS feed integration.)
      </p>
      {(!comps || comps.length === 0) ? (
        <div className="text-gray-500 text-sm">No comparable active listings found in the system yet.</div>
      ) : (
        <div className="divide-y divide-[#546E7A]/20">
          {comps.map((c) => (
            <div key={c.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm truncate">{c.address}</div>
                <div className="text-gray-500 text-xs truncate">
                  {[c.city, c.state].filter(Boolean).join(', ')}
                  {' · '}
                  {[c.beds != null ? `${c.beds}bd` : null, c.baths != null ? `${c.baths}ba` : null,
                    c.sqft != null ? `${Number(c.sqft).toLocaleString()}sqft` : null].filter(Boolean).join(' / ')}
                </div>
              </div>
              <div className="text-[#00E676] font-semibold text-sm shrink-0">{money(c.price)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Draft detail (/listing-lab/:propertyId)                             */
/* ------------------------------------------------------------------ */
function DraftDetail({ propertyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);          // tunable attributes
  const [scoring, setScoring] = useState(false);
  const [demand, setDemand] = useState(null);
  const [points, setPoints] = useState([]);
  const [confirmActivate, setConfirmActivate] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await authFetch(`${API}/api/listing-lab/${propertyId}`);
        if (!res.ok) {
          setError(res.status === 404 ? 'Draft not found (it may have been activated).' : 'Could not load this draft.');
          setLoading(false);
          return;
        }
        const d = await res.json();
        if (cancelled) return;
        setData(d);
        setDemand(d.demand);
        setPoints(d.demand_at_price || []);
        const dr = d.draft || {};
        setForm({
          price: dr.price ?? '', beds: dr.beds ?? '', baths: dr.baths ?? '',
          sqft: dr.sqft ?? '', school_rating: dr.school_rating ?? '',
          garage_spaces: dr.garage_spaces ?? '', hoa_monthly: dr.hoa_monthly ?? '',
          year_built: dr.year_built ?? '', stories: dr.stories ?? '',
          bathrooms_full: dr.bathrooms_full ?? '', bathrooms_half: dr.bathrooms_half ?? '',
          // Tri-state: null means the draft doesn't state it, which the scorer treats as
          // unknown. Keep null (not false) so a dry run reflects the real listing.
          pool: dr.pool ?? null,
          waterfront: dr.waterfront ?? null,
          gated_community: dr.gated_community ?? null,
          senior_community: dr.senior_community ?? null,
          accessibility_features: dr.accessibility_features ?? null,
          dogs_allowed: dr.dogs_allowed ?? null,
          cats_allowed: dr.cats_allowed ?? null,
          pool_features: dr.pool_features ?? '',
          waterfront_features: dr.waterfront_features ?? '',
          appliances: dr.appliances ?? '',
          community_amenities: dr.community_amenities ?? '',
        });
      } catch (e) {
        if (!cancelled) setError('Network error.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  const rescore = useCallback(async (overrides) => {
    setScoring(true);
    try {
      const body = {};
      ['price', 'beds', 'sqft', 'garage_spaces', 'year_built', 'stories',
       'bathrooms_full', 'bathrooms_half'].forEach((f) => {
        if (overrides[f] !== '' && overrides[f] != null) body[f] = parseInt(overrides[f]);
      });
      ['baths', 'school_rating', 'hoa_monthly'].forEach((f) => {
        if (overrides[f] !== '' && overrides[f] != null) body[f] = parseFloat(overrides[f]);
      });
      // Booleans: send true/false, omit null. Omitting keeps the draft's own value, and a
      // draft that never stated the attribute stays unknown rather than becoming a "no".
      ['pool', 'waterfront', 'gated_community', 'senior_community',
       'accessibility_features', 'dogs_allowed', 'cats_allowed'].forEach((f) => {
        if (overrides[f] === true || overrides[f] === false) body[f] = overrides[f];
      });
      ['pool_features', 'waterfront_features', 'appliances', 'community_amenities'].forEach((f) => {
        if (overrides[f] !== '' && overrides[f] != null) body[f] = overrides[f];
      });
      const res = await authFetch(`${API}/api/listing-lab/${propertyId}/score`, {
        method: 'POST', body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        setDemand(d.demand);
        setPoints(d.demand_at_price || []);
      }
    } finally {
      setScoring(false);
    }
  }, [propertyId]);

  const activate = async () => {
    setActivating(true);
    try {
      const res = await authFetch(`${API}/api/listing-lab/${propertyId}/activate`, { method: 'POST' });
      if (res.ok) {
        navigate(`/properties/${propertyId}`);
      } else {
        setError('Activation failed. Please try again.');
        setConfirmActivate(false);
      }
    } finally {
      setActivating(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center text-[#00D4FF]">Loading draft…</div>;
  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="bg-[#1A2744] rounded-xl border border-red-600/40 p-8 text-center max-w-md">
          <div className="text-red-400 font-semibold mb-2">Listing Lab</div>
          <div className="text-gray-300 text-sm mb-4">{error}</div>
          <Link to="/listing-lab" className="inline-block px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg text-sm">← Back to Listing Lab</Link>
        </div>
      </div>
    );
  }

  const dr = data.draft || {};
  const curPrice = form && form.price !== '' ? Number(form.price) : dr.price;
  const triField = (label, key) => (
    <div>
      <label className="block text-[11px] text-[#B0BEC5] mb-1">{label}</label>
      <select
        value={form[key] === true ? 'yes' : form[key] === false ? 'no' : ''}
        onChange={(e) => {
          const v = e.target.value;
          setForm({ ...form, [key]: v === 'yes' ? true : v === 'no' ? false : null });
        }}
        className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#00D4FF]">
        <option value="">Not stated</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
  const textField = (label, key, placeholder) => (
    <div>
      <label className="block text-[11px] text-[#B0BEC5] mb-1">{label}</label>
      <input type="text" value={form[key] ?? ''} placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]" />
    </div>
  );
  const numField = (label, key, step) => (
    <div>
      <label className="block text-[11px] text-[#B0BEC5] mb-1">{label}</label>
      <input type="number" step={step || 1} value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-2 py-1.5 bg-[#0A0E27] border border-[#546E7A]/50 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#00D4FF]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* header */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="min-w-0">
            <Link to="/listing-lab" className="text-gray-400 hover:text-[#00D4FF] text-sm">← Listing Lab</Link>
            <h1 className="text-xl md:text-2xl font-bold text-white mt-1 flex items-center gap-2 flex-wrap">
              <span className="truncate">{dr.address || 'Draft'}</span>
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30">DRAFT</span>
            </h1>
            <div className="text-gray-400 text-sm">{[dr.city, dr.state, dr.zip].filter(Boolean).join(', ')}</div>
          </div>
          <button onClick={() => setConfirmActivate(true)}
            className="px-4 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] text-sm whitespace-nowrap">
            Activate listing →
          </button>
        </div>

        <div className="bg-[#0A0E27] border border-[#00D4FF]/20 rounded-lg px-3 py-2 text-xs text-[#B0BEC5] mb-5">
          🔒 This is a private pre-listing draft. It is excluded from buyer match boards, reports, and search — buyers never see it while it’s a draft.
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* left: tuning + demand */}
          <div className="space-y-5">
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-semibold text-lg">Tune Price & Attributes</h2>
                {scoring && <span className="text-[#00D4FF] text-xs">scoring…</span>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {numField('List Price ($)', 'price', 1000)}
                {numField('Beds', 'beds', 1)}
                {numField('Baths', 'baths', 0.5)}
                {numField('Sqft', 'sqft', 50)}
                {numField('School Rating (0-10)', 'school_rating', 0.1)}
                {numField('Garage Spaces', 'garage_spaces', 1)}
                {numField('HOA ($/mo)', 'hoa_monthly', 10)}
                {numField('Year Built', 'year_built', 1)}
                {numField('Stories', 'stories', 1)}
                {numField('Full Baths', 'bathrooms_full', 1)}
                {numField('Half Baths', 'bathrooms_half', 1)}
              </div>

              {/* Expanded attributes — toggling any of these re-scores demand */}
              <div className="mt-4 pt-4 border-t border-[#546E7A]/20">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Features</div>
                <div className="grid grid-cols-2 gap-3">
                  {triField('Pool', 'pool')}
                  {triField('Waterfront', 'waterfront')}
                  {triField('Gated', 'gated_community')}
                  {triField('Senior (55+)', 'senior_community')}
                  {triField('Accessibility', 'accessibility_features')}
                  {triField('Dogs allowed', 'dogs_allowed')}
                  {triField('Cats allowed', 'cats_allowed')}
                </div>
                <div className="grid grid-cols-1 gap-3 mt-3">
                  {textField('Appliances included', 'appliances', 'Refrigerator, washer, dryer')}
                  {textField('Community amenities', 'community_amenities', 'Pool, clubhouse, tennis')}
                </div>
              </div>
              <button onClick={() => rescore(form)} disabled={scoring}
                className="mt-4 w-full px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] disabled:opacity-50 text-sm">
                {scoring ? 'Re-scoring…' : 'Re-score demand'}
              </button>
            </div>

            <DemandPanel demand={demand} evaluated={data.buyers_evaluated} />
          </div>

          {/* right: demand-at-price + comps */}
          <div className="space-y-5">
            <DemandAtPrice points={points} currentPrice={curPrice} />
            <Comparables comps={data.comparables} />
          </div>
        </div>
      </div>

      {/* activation confirm */}
      {confirmActivate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => !activating && setConfirmActivate(false)}>
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/40 p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-2">Activate this listing?</h3>
            <p className="text-[#B0BEC5] text-sm mb-3">
              Activation makes <b className="text-white">{dr.address}</b> a normal listing in SchemeSync and
              scores it for your buyers so it appears on their match boards.
            </p>
            <div className="bg-[#0A0E27] border border-[#FF6B35]/40 rounded-lg px-3 py-2 text-xs text-[#FFB199] mb-4">
              ⚠️ This makes it live in <b>SchemeSync only — it is NOT an MLS entry</b>. To list it on the MLS,
              enter it in your MLS separately.
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmActivate(false)} disabled={activating}
                className="px-4 py-2 text-gray-300 hover:text-white text-sm">Cancel</button>
              <button onClick={activate} disabled={activating}
                className="px-5 py-2 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 text-sm">
                {activating ? 'Activating…' : 'Activate in SchemeSync'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Entry                                                               */
/* ------------------------------------------------------------------ */
export default function ListingLab() {
  const { user } = useAuth();
  const { propertyId } = useParams();

  if (!canView(user?.role)) {
    return (
      <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <h1 className="text-white text-2xl font-bold mb-2">Listing Lab</h1>
            <p className="text-[#B0BEC5]">This page is available to agents and brokers only.</p>
          </div>
        </div>
      </div>
    );
  }

  return propertyId ? <DraftDetail propertyId={propertyId} /> : <DraftsList />;
}
